'use es6';

import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { List, Map as ImmutableMap } from 'immutable';
import { View } from 'foundations-components/components/View';
import { Alert } from 'foundations-components/transitional/components/Alert';
import ExchangeInboxForm from '../form/ExchangeInboxForm';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import StepInterface from './StepInterface';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import ExchangeInboxRecord from '../../data/ExchangeInboxRecord';
import { getServerValidationErrorMessages } from 'InboxConnectUtils/utils/InboxConnectionUtils';
import { TEST_MESSAGE_STEP, EXCHANGE_INBOX_STEP } from '../../constants/InboxConnectStepKeys';
import { AUTODISCOVERY_FAILURE } from '../../constants/InboxErrors';
import { transformGenericToReturnableExchangeAccount } from '../../data/transforms';
import BasicAuthConnectErrorTitle from '../errors/BasicAuthConnectErrorTitle';
import ProviderTypes from 'InboxConnectUtils/constants/ProviderTypes';
import BasicAuthConnectError from '../errors/BasicAuthConnectError';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LANG_ROUTE = 'InboxConnectUtils.wizard.imapInbox.';
class ExchangeInboxStep extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      didRequestSubmit: false,
      didExpandAdvancedSettings: false,
      didAutoDiscoveryFail: false,
      value: ExchangeInboxRecord(),
      inboxErrors: List()
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.openAdvancedSettings = this.openAdvancedSettings.bind(this);
  }
  componentDidMount() {
    this.props.trackingClient.trackStepView(EXCHANGE_INBOX_STEP);
  }
  componentDidUpdate() {
    const {
      inbox: {
        id
      },
      inboxSettings
    } = this.props;
    const {
      value: {
        email
      },
      value,
      inboxErrors
    } = this.state;
    if (id) {
      this.props.trackingClient.trackStepAction(EXCHANGE_INBOX_STEP, 'submit', {
        subAction: 'completed'
      });
      this.props.setEmailAddress(email);
      this.props.setShownStep(TEST_MESSAGE_STEP);
      this.setState({
        didRequestSubmit: false
      });
      return;
    }
    if (0 < inboxErrors.size) {
      this.setState({
        didRequestSubmit: false
      });
    }
    const discoveredSettings = inboxSettings && inboxSettings.get(email);
    if (discoveredSettings) {
      this.setState(Object.assign({}, this.getNextStateParts(value, discoveredSettings), {
        didRequestDiscovery: false
      }));
      this.props.trackingClient.trackStepAction(EXCHANGE_INBOX_STEP, 'autodiscovery');
    }
  }
  componentWillUnmount() {
    this.clearErrors();
  }
  clearErrors() {
    this.setState({
      inboxErrors: this.state.inboxErrors.clear()
    });
  }
  clearError(index) {
    this.setState({
      inboxErrors: this.state.inboxErrors.delete(index)
    });
  }
  addError(inboxError) {
    this.setState({
      inboxErrors: this.state.inboxErrors.push(inboxError)
    });
  }
  getNextStateParts(value, discoveredSettings) {
    const didAutoDiscoveryFail = !(discoveredSettings && discoveredSettings.exchangeUrl && discoveredSettings.domain);
    return {
      didAutoDiscoveryFail,
      value: value.merge(discoveredSettings)
    };
  }
  handleChange({
    target: {
      value: newValue
    }
  }) {
    this.setState({
      value: newValue
    });
  }
  handleSubmit(evt) {
    evt.preventDefault();
    const {
      trackingClient,
      service,
      setShownStep,
      setConnectedAccount
    } = this.props;
    const {
      value
    } = this.state;
    trackingClient.trackStepAction(EXCHANGE_INBOX_STEP, 'submit', {
      subAction: 'creating'
    });
    this.clearErrors();
    const processInbox = inbox => {
      const {
        error
      } = inbox;
      if (error) {
        this.addError(error);
        if (error === AUTODISCOVERY_FAILURE) {
          this.setState({
            didAutoDiscoveryFail: true
          });
        }
        return;
      }
      setConnectedAccount(transformGenericToReturnableExchangeAccount(inbox));
      const {
        value: {
          email
        }
      } = this.state;
      this.props.setEmailAddress(email);
      this.props.trackingClient.trackStepAction(EXCHANGE_INBOX_STEP, 'submit', {
        subAction: 'completed'
      });
      setShownStep(TEST_MESSAGE_STEP);
    };
    this.setState({
      didRequestSubmit: true
    });
    service.createExchangeInbox(value).then(processInbox).catch(() => this.setState({
      didAutoDiscoveryFail: true
    })).finally(() => this.setState({
      didRequestSubmit: false
    })).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }
  openAdvancedSettings() {
    this.setState({
      didExpandAdvancedSettings: true
    });
  }
  render() {
    const {
      didAutoDiscoveryFail,
      didExpandAdvancedSettings,
      didRequestSubmit,
      value,
      inboxErrors
    } = this.state;
    const handleOnClose = index => () => this.clearError(index);
    return /*#__PURE__*/_jsx(TFlex, {
      justify: "center",
      direction: "row",
      "data-selenium": "exchange-inbox-step",
      children: /*#__PURE__*/_jsxs(View, {
        textAlign: "center",
        maxInlineSize: "420px",
        "data-selenium": "exchange-inbox-step",
        children: [/*#__PURE__*/_jsx("h2", {
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: `${LANG_ROUTE}titleExchange`
          })
        }), /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "InboxConnectUtils.genericInboxForm.advancedSettingsHelp_jsx",
          elements: {
            UILink: Link
          },
          options: {
            href: 'https://knowledge.hubspot.com/email-tracking/connect-your-inbox-to-hubspot#connect-your-exchange-account',
            external: true
          }
        }), inboxErrors && inboxErrors.map(errorCode => /*#__PURE__*/_jsx(Alert, {
          marginBlockEnd: "200",
          textAlign: "start",
          closeable: true,
          "data-errorcode": errorCode,
          onClose: handleOnClose,
          titleText: /*#__PURE__*/_jsx(BasicAuthConnectErrorTitle, {
            providerType: ProviderTypes.exchange,
            errorCode: errorCode
          }),
          type: "danger",
          children: /*#__PURE__*/_jsx(BasicAuthConnectError, {
            errorCode: errorCode,
            providerType: ProviderTypes.exchange
          })
        }, errorCode)), didRequestSubmit ? /*#__PURE__*/_jsx(LoadingSpinner, {
          grow: true,
          marginBlock: "1000"
        }) : /*#__PURE__*/_jsx(ExchangeInboxForm, {
          disabled: didRequestSubmit,
          isLoading: didRequestSubmit,
          didExpandAdvancedSettings: didExpandAdvancedSettings,
          didAutoDiscoveryFail: didAutoDiscoveryFail,
          handleExpand: this.openAdvancedSettings,
          onChange: this.handleChange,
          onSubmit: this.handleSubmit,
          serverValidationErrors: getServerValidationErrorMessages(inboxErrors),
          submitText: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: `${LANG_ROUTE}button`
          }),
          value: value
        })]
      })
    });
  }
}
ExchangeInboxStep.defaultProps = {
  inbox: ExchangeInboxRecord(),
  inboxSettings: ImmutableMap()
};
ExchangeInboxStep.propTypes = Object.assign({}, StepInterface, {
  inbox: PropTypes.instanceOf(ExchangeInboxRecord).isRequired,
  inboxSettings: PropTypes.instanceOf(ImmutableMap).isRequired
});
export default ExchangeInboxStep;